import { useMemo, useState } from "react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { fmtN } from "../../lib/funnel/helpers";
import { PRIMARY, FG1, FG3, MUTED, BORDER, GOLD } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import { InfoButton } from "../../lib/funnel/InfoButton";
import type { FilterPillSpec } from "../../components/FilterPills";
import { MAX_BUCKET_IDX, ACTIVITY_BUCKETS, bucketForDay } from "./activitySql";
import type { ActivityDaily, ActivityEventType } from "./activitySql";
import type { BaselineRow } from "./sql";
import { daysBetween } from "../../lib/funnel/helpers";

// % of cohort active on day D-since-signup. "Active" can mean any event, or a
// specific event type — the metric you A/B against matters. Markers at the
// classic checkpoints D1 / D7 / D14 / D30.

type ActivityScope = "any" | "summary" | "transcript" | "chat";
type DenomMode = "censored" | "eligible";

const DENOM_LABEL: Record<DenomMode, string> = {
  censored: "Full cohort",
  eligible: "Eligible at bucket",
};

const SCOPE_LABEL: Record<ActivityScope, string> = {
  any: "Any event",
  summary: "Summary",
  transcript: "Transcript",
  chat: "Ask Marloo",
};
const SCOPE_EVENT: Record<ActivityScope, ActivityEventType | null> = {
  any: null,
  summary: "summary",
  transcript: "transcript",
  chat: "chat",
};

const MAX_DAY = 89;

export function RetentionCurve({
  pagePills,
  users,
  daily,
  cohortSize,
  loading,
  compareUsers,
  compareDaily,
  compareCohortSize,
  comparePills,
}: {
  pagePills?: FilterPillSpec[];
  users: BaselineRow[];
  daily: ActivityDaily[];
  cohortSize: number;
  loading: boolean;
  compareUsers?: BaselineRow[] | null;
  compareDaily?: ActivityDaily[] | null;
  compareCohortSize?: number | null;
  comparePills?: FilterPillSpec[];
}) {
  const compareOn = !!compareUsers && !!compareDaily && compareCohortSize != null;
  const [scope, setScope] = useState<ActivityScope>("any");
  const [denomMode, setDenomMode] = useState<DenomMode>("eligible");

  // Per-user observation horizon = how many full days of observation we have
  // for this user, given their signup date. Clamped to MAX_DAY since the SQL
  // only returns events up to day 89.
  const todayStr = new Date().toISOString().slice(0, 10);
  function buildHorizonMap(uList: BaselineRow[]): Map<string, number> {
    const m = new Map<string, number>();
    for (const u of uList) {
      const days = daysBetween(u.signup_date_utc, todayStr);
      m.set(u.user_external_id, Math.min(MAX_DAY, Math.max(0, days)));
    }
    return m;
  }
  const userMaxObservableDay = useMemo(() => buildHorizonMap(users), [users]);
  const compareHorizon = useMemo(() => compareUsers ? buildHorizonMap(compareUsers) : null, [compareUsers]);

  // For each day D, count distinct users who had >=1 matching event on any
  // day >= D within the 90d observation. (Classic "still active by D" curve.)
  // In `eligible` mode, denominator at day D is users with >= D days of
  // observation (signed up >= D days ago) so the curve isn't pulled down by
  // recent signups who physically can't have reached day D yet.
  function computeCurve(
    uList: BaselineRow[],
    dailySrc: ActivityDaily[],
    cohortN: number,
    horizonMap: Map<string, number>,
  ) {
    // Activity stream is pre-aggregated into 5 named retention buckets
    // server-side (see ACTIVITY_BUCKETS). Each row's bucket_idx is the bucket
    // directly. Eligibility uses bucketForDay() on the user's day-horizon.
    const bucketCount = MAX_BUCKET_IDX + 1;
    const userMaxBucket: Map<string, number> = new Map();
    const eventFilter = SCOPE_EVENT[scope];
    for (const d of dailySrc) {
      if (eventFilter && d.event_type !== eventFilter) continue;
      if (d.bucket_idx < 0 || d.bucket_idx > MAX_BUCKET_IDX) continue;
      const cur = userMaxBucket.get(d.user_external_id) ?? -1;
      if (d.bucket_idx > cur) userMaxBucket.set(d.user_external_id, d.bucket_idx);
    }
    const eligibleByBucket: number[] = Array.from({ length: bucketCount }, () => 0);
    for (const u of uList) {
      const horizonBucket = bucketForDay(horizonMap.get(u.user_external_id) ?? 0);
      if (horizonBucket < 0) continue;
      for (let b = 0; b <= horizonBucket && b < bucketCount; b++) {
        eligibleByBucket[b] = (eligibleByBucket[b] ?? 0) + 1;
      }
    }
    const activeByBucket: number[] = Array.from({ length: bucketCount }, () => 0);
    for (const [userId, maxB] of userMaxBucket.entries()) {
      const horizonBucket = bucketForDay(horizonMap.get(userId) ?? MAX_DAY);
      const upper = denomMode === "eligible" ? Math.min(maxB, horizonBucket < 0 ? maxB : horizonBucket) : maxB;
      for (let b = 0; b <= upper && b < bucketCount; b++) {
        activeByBucket[b] = (activeByBucket[b] ?? 0) + 1;
      }
    }
    return Array.from({ length: bucketCount }, (_, b) => {
      const denom = denomMode === "eligible" ? (eligibleByBucket[b] ?? 0) : cohortN;
      const num = activeByBucket[b] ?? 0;
      return {
        bucketIdx: b,
        label: ACTIVITY_BUCKETS[b]?.label ?? `Bucket ${b}`,
        pct: denom === 0 ? 0 : (num / denom) * 100,
        n: num,
        denom,
      };
    });
  }

  const data = useMemo(
    () => computeCurve(users, daily, cohortSize, userMaxObservableDay),
    [daily, scope, cohortSize, denomMode, users, userMaxObservableDay],
  );
  const compareData = useMemo(
    () => compareOn && compareUsers && compareDaily && compareCohortSize != null && compareHorizon
      ? computeCurve(compareUsers, compareDaily, compareCohortSize, compareHorizon)
      : null,
    [compareOn, compareUsers, compareDaily, compareCohortSize, compareHorizon, scope, denomMode],
  );

  const mergedData = useMemo(() => {
    if (!compareData) return data;
    return data.map((p, i) => ({ ...p, comparePct: compareData[i]?.pct ?? 0, compareN: compareData[i]?.n ?? 0, compareDenom: compareData[i]?.denom ?? 0 }));
  }, [data, compareData]);

  const checkpoints = data.map((point, idx) => ({
    label: point.label,
    point,
    comparePoint: compareData ? compareData[idx] : null,
  }));

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    ...(compareOn ? [{ kind: "mode" as const, label: "vs comparison" }, ...(comparePills ?? [])] : []),
    { kind: "mode" as const, label: `Active = ${SCOPE_LABEL[scope]}` },
    { kind: "mode" as const, label: `Denom = ${DENOM_LABEL[denomMode]}` },
  ];

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Retention curve
            <InfoButton label="How to read this">
              <p style={{ margin: "0 0 6px" }}>
                For each day D since sign-up, the line shows the <strong>% of the cohort still active by day D</strong>.
                A user counts as still active by D if their <em>most recent</em> matching event happened on day D or
                later within the 90-day observation window. The curve only decreases — once a user goes quiet, they
                stay quiet.
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Active = </strong> the user did at least one event of the selected type. The
                <em> Scope</em> toggle changes which events count:
              </p>
              <ul style={{ margin: "0 0 6px", paddingLeft: 18 }}>
                <li><strong>Any event</strong>: transcript, summary, custom template, calendar, Ask Marloo, document, task, client, or form. Broadest signal, easiest to keep high.</li>
                <li><strong>Summary</strong>: only meeting summaries (non-demo). The core value event — best A/B anchor for "did they actually get value?".</li>
                <li><strong>Transcript</strong>: only recorded meetings. A weaker signal (you can transcribe without ever opening the summary).</li>
                <li><strong>Ask Marloo</strong>: only chat events. Measures depth of engagement, not breadth.</li>
              </ul>
              <p style={{ margin: "0 0 6px" }}>
                Markers and the side card pin D1 / D7 / D14 / D30 — the four checkpoints most worth tracking in an
                onboarding A/B. New flows can lift early activation but tank D30 retention, or vice versa.
              </p>
              <p style={{ margin: 0, color: FG3 }}>
                <strong>Denominator</strong>: in <em>Full cohort</em> mode the denominator at every day is the entire
                cohort, so recent signups (who haven't been observed long enough to reach day D yet) count as
                inactive and pull the right side of the curve down. In <em>Eligible at day D</em> mode the
                denominator at each day is restricted to users with at least D days of observation since signup —
                a sharper read of true long-tail retention, at the cost of a shrinking denominator as you go right.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={
          loading
            ? "Loading activity stream…"
            : `Share of ${denomMode === "eligible" ? "eligible users" : "the cohort"} still active by each retention bucket since sign-up (≥1 ${scope === "any" ? "event" : SCOPE_LABEL[scope].toLowerCase()} in this bucket or later)${denomMode === "eligible" ? ". Denominator shrinks at later buckets — only users with that much observation time count" : ". Right-censored: recent signups can't reach later buckets yet and pull the curve down"}.`
        }
        filterPills={pills}
        headerExtra={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div role="group" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
              {(["any", "summary", "transcript", "chat"] as ActivityScope[]).map((s) => {
                const active = scope === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none", cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {SCOPE_LABEL[s]}
                  </button>
                );
              })}
            </div>
            <div role="group" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
              {(["censored", "eligible"] as DenomMode[]).map((m) => {
                const active = denomMode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDenomMode(m)}
                    title={m === "censored" ? "Denominator is the full cohort at every day (right-censored)" : "Denominator at day D is users with ≥ D days of observation"}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none", cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {DENOM_LABEL[m]}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body" style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 16, alignItems: "stretch" }}>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={mergedData} margin={{ top: 8, right: 16, bottom: 24, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={MUTED} opacity={0.3} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: FG3 }} />
              <YAxis tick={{ fontSize: 11, fill: FG3 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                content={(props) => {
                  const { active, payload, label } = props as unknown as { active?: boolean; payload?: { payload: typeof mergedData[number] }[]; label?: string };
                  if (!active || !payload || payload.length === 0) return null;
                  const point = payload[0]?.payload;
                  if (!point) return null;
                  const idx = data.findIndex((p) => p.label === point.label);
                  const cpoint = compareData && idx >= 0 ? compareData[idx] : null;
                  const denomLabel = denomMode === "eligible" ? "eligible users" : "cohort";
                  const excluded = denomMode === "eligible" ? Math.max(0, cohortSize - point.denom) : 0;
                  const cExcluded = denomMode === "eligible" && cpoint && compareCohortSize != null
                    ? Math.max(0, compareCohortSize - cpoint.denom)
                    : 0;
                  return (
                    <div style={{ background: "var(--white)", border: "1px solid var(--border-default)", borderRadius: 7, padding: "8px 10px", fontSize: 12, boxShadow: "var(--shadow-md)", lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 600 }}>{label}</div>
                      <div style={{ color: PRIMARY, marginTop: 2 }}>Baseline: <strong>{point.pct.toFixed(1)}%</strong> of {denomLabel}</div>
                      <div style={{ color: FG3, marginTop: 2, fontSize: 11 }}>
                        {fmtN(point.n)} of {fmtN(point.denom)}
                        {excluded > 0 && ` (${fmtN(excluded)} excluded, not yet observed)`}
                      </div>
                      {cpoint && (
                        <>
                          <div style={{ color: GOLD, marginTop: 4 }}>Comparison: <strong>{cpoint.pct.toFixed(1)}%</strong></div>
                          <div style={{ color: FG3, marginTop: 2, fontSize: 11 }}>
                            {fmtN(cpoint.n)} of {fmtN(cpoint.denom)}
                            {cExcluded > 0 && ` (${fmtN(cExcluded)} excluded)`}
                          </div>
                          <div style={{ color: FG3, marginTop: 4, fontSize: 11 }}>Δ {(cpoint.pct - point.pct >= 0 ? "+" : "")}{(cpoint.pct - point.pct).toFixed(1)}pp</div>
                        </>
                      )}
                    </div>
                  );
                }}
              />
              <Line type="monotone" dataKey="pct" stroke={PRIMARY} strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />
              {compareOn && <Line type="monotone" dataKey="comparePct" stroke={GOLD} strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
          <div style={{ color: FG3, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
            Checkpoints
          </div>
          {checkpoints.map((c) => (
            <div key={c.label} style={{ padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontWeight: 600 }}>{c.label}</span>
                <span style={{ color: PRIMARY, fontWeight: 600, fontSize: 16 }}>
                  {c.point ? `${c.point.pct.toFixed(1)}%` : "-"}
                </span>
              </div>
              <div style={{ color: FG3, fontSize: 10, marginTop: 2 }}>
                {c.point ? `${fmtN(c.point.n)} of ${fmtN(c.point.denom)} users` : ""}
              </div>
              {c.comparePoint && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${GOLD}66`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ color: GOLD, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Cmp</span>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: GOLD, fontWeight: 600, fontSize: 14 }}>{c.comparePoint.pct.toFixed(1)}%</div>
                    <div style={{ color: FG3, fontSize: 10 }}>
                      {fmtN(c.comparePoint.n)} of {fmtN(c.comparePoint.denom)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
